import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../lib/api";
import { getBenefitIcon } from "../lib/benefitIcons";

const DEFAULTS = {
    highlights: [
        { icon: "euro", title: "Faire Vergütung", text: "Faire, leistungsgerechte Bezahlung – passend zu Erfahrung, Qualifikation und Verantwortung." },
        { icon: "calendar", title: "30 Urlaubstage", text: "30 Tage Erholung pro Jahr – für Familie, Freizeit und neue Energie." },
        { icon: "gift", title: "Weihnachtsgeld", text: "Zusätzliche Sonderzahlung als Anerkennung für deinen Einsatz." },
        { icon: "bike", title: "Dienstrad / JobBike", text: "Dein Wunschrad oder E-Bike bequem über uns leasen – inklusive Versicherung." },
    ],
    additional: [
        { icon: "layers", title: "Abwechslungsreiche Aufgaben", text: "Umwelttechnik zum Anfassen – kein Arbeitstag ist wie der andere." },
        { icon: "users", title: "Kurze Entscheidungswege", text: "Direkte Abstimmung statt langer Konzernwege." },
        { icon: "zap", title: "Moderne Technik & spannende Projekte", text: "Von Baustellen und Tunnelbau bis zu Industrie- und Infrastrukturprojekten." },
        { icon: "shield", title: "Sicherer Arbeitsplatz", text: "Ein wachsendes Unternehmen mit langfristiger Perspektive." },
    ],
};

const colsClass = (count, large) => {
    if (count <= 2) return large ? "grid sm:grid-cols-2 gap-5 md:gap-6 mb-6 md:mb-8" : "grid sm:grid-cols-2 gap-3 md:gap-4";
    if (count === 3 || count === 5 || count === 6) return large ? "grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-6 md:mb-8" : "grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4";
    return large ? "grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-6 md:mb-8" : "grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4";
};

export default function Benefits() {
    const [data, setData] = useState(DEFAULTS);

    useEffect(() => {
        axios
            .get(`${API}/benefits`)
            .then((r) => {
                if (r.data?.highlights?.length) setData(r.data);
            })
            .catch(() => {});
    }, []);

    const highlights = data.highlights || [];
    const additional = data.additional || [];

    return (
        <section
            id="was-wir-bieten"
            data-testid="benefits-section"
            className="py-20 md:py-28 bg-moebus-deep relative overflow-hidden"
        >
            {/* Decorative gradient blobs */}
            <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-3xl"></div>
            <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-blue-500/10 blur-3xl"></div>
            <div className="absolute inset-0 dot-grid opacity-30"></div>

            <div className="relative container mx-auto px-4 md:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="max-w-3xl mb-14 md:mb-20"
                >
                    <div className="text-xs tracking-[0.3em] uppercase text-emerald-400 font-semibold mb-4">
                        Was wir bieten
                    </div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight text-balance">
                        Faire Bedingungen. Echtes Team. Solide Perspektive.
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed">
                        Bei uns bekommst du nicht nur einen Job – du bekommst ein Zuhause für deine Arbeit.
                        Hier sind die Punkte, die zählen.
                    </p>
                </motion.div>

                {/* Highlights - large cards */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    variants={{
                        hidden: {},
                        visible: { transition: { staggerChildren: 0.12 } },
                    }}
                    className={colsClass(highlights.length, true)}
                >
                    {highlights.map(({ icon, title, text }) => {
                        const Icon = getBenefitIcon(icon);
                        return (
                            <motion.div
                                key={title}
                                variants={{
                                    hidden: { opacity: 0, y: 30 },
                                    visible: {
                                        opacity: 1,
                                        y: 0,
                                        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                                    },
                                }}
                                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                                data-testid={`benefit-highlight-${title}`}
                                className="relative bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur border border-white/10 rounded-xl p-7 md:p-8 hover:border-emerald-400/40 transition-all group overflow-hidden"
                            >
                                <div className="absolute -top-12 -right-12 w-32 h-32 bg-emerald-400/10 rounded-full blur-2xl group-hover:bg-emerald-400/20 transition-all"></div>
                                <div className="relative">
                                    <div className="w-12 h-12 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-5 group-hover:bg-emerald-400 group-hover:text-moebus-deep transition-all">
                                        <Icon size={24} strokeWidth={1.8} />
                                    </div>
                                    <h3 className="font-heading font-bold text-white text-xl md:text-2xl">
                                        {title}
                                    </h3>
                                    <p className="mt-3 text-sm md:text-base text-slate-300 leading-relaxed">
                                        {text}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </motion.div>

                {/* Additional benefits - smaller row */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    variants={{
                        hidden: {},
                        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
                    }}
                    className={colsClass(additional.length, false)}
                >
                    {additional.map(({ icon, title, text }) => {
                        const Icon = getBenefitIcon(icon);
                        return (
                            <motion.div
                                key={title}
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: {
                                        opacity: 1,
                                        y: 0,
                                        transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                                    },
                                }}
                                data-testid={`benefit-${title}`}
                                className="bg-white/5 border border-white/10 rounded-lg p-5 hover:bg-white/[0.07] transition-colors"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-md bg-white/5 text-blue-400 flex items-center justify-center flex-shrink-0">
                                        <Icon size={18} strokeWidth={1.8} />
                                    </div>
                                    <div>
                                        <div className="font-heading font-semibold text-white text-base">
                                            {title}
                                        </div>
                                        <div className="mt-1 text-sm text-slate-400 leading-relaxed">{text}</div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
}
